/**
 * Route translation dictionary and helpers for URL-based i18n.
 *
 * Public prefix aliases take precedence over first-segment translation.
 * Sub-paths (dynamic params, slugs) pass through unchanged, except for industry
 * category slugs which are locale-dependent.
 */

import { relocalizeIndustrySlug } from '@/lib/industry-categories';

export const SUPPORTED_LOCALES = ['de', 'en'] as const;
export type Locale = (typeof SUPPORTED_LOCALES)[number];
export const DEFAULT_LOCALE: Locale = 'de';

/** Public URL prefixes differ from the internal filesystem paths. */
export const PREFIX_ALIASES: ReadonlyArray<{
  internal: string;
  public: Record<Locale, string>;
}> = [
  { internal: '/insolvenzticker', public: { de: '/entdecken/insolvenzen', en: '/explore/insolvencies' } },
  { internal: '/neugruendungen', public: { de: '/entdecken/neugruendungen', en: '/explore/new-companies' } },
  { internal: '/vergabe', public: { de: '/entdecken/vergabe', en: '/explore/tenders' } },
  { internal: '/industry', public: { de: '/entdecken/industrie', en: '/explore/industry' } },
  { internal: '/account/notifications', public: { de: '/notifications', en: '/notifications' } },
  { internal: '/account/usage', public: { de: '/usage', en: '/usage' } },
  { internal: '/account/api-keys', public: { de: '/developer/api-keys', en: '/developer/api-keys' } },
  { internal: '/mcp', public: { de: '/developer/mcp', en: '/developer/mcp' } },
];

const INTERNAL_PREFIX_ALIASES = [...PREFIX_ALIASES].sort(
  (a, b) => b.internal.length - a.internal.length,
);
function aliasesForLocale(locale: Locale): typeof PREFIX_ALIASES {
  return [...PREFIX_ALIASES].sort(
    (a, b) => b.public[locale].length - a.public[locale].length,
  );
}

const PUBLIC_PREFIX_ALIASES: Record<Locale, typeof PREFIX_ALIASES> = {
  de: aliasesForLocale('de'),
  en: aliasesForLocale('en'),
};

function matchesPrefix(path: string, prefix: string): boolean {
  return path === prefix || path.startsWith(`${prefix}/`);
}

/**
 * Bidirectional segment map: internal segment -> localized segment per locale.
 * Only public-facing segments that need translation are listed here.
 * Admin/account/metrics etc. keep their English names in all locales.
 */
const SEGMENT_MAP: Record<string, Record<Locale, string>> = {
  company: { de: 'unternehmen', en: 'company' },
  search: { de: 'suche', en: 'search' },
  overview: { de: 'uebersicht', en: 'overview' },
  activity: { de: 'aktivitaet', en: 'activity' },
  // Retained for delocalizing legacy public URLs before canonical redirects.
  industry: { de: 'industrie', en: 'industry' },
  region: { de: 'region', en: 'region' },
  faq: { de: 'faq', en: 'faq' },
  docs: { de: 'dokumentation', en: 'docs' },
  impressum: { de: 'impressum', en: 'imprint' },
  datenqualitaet: { de: 'datenqualitaet', en: 'data-quality' },
  sources: { de: 'datenquellen', en: 'sources' },
  insolvenzticker: { de: 'insolvenzticker', en: 'insolvency-ticker' },
  vergabe: { de: 'vergabe', en: 'tenders' },
  contact: { de: 'kontakt', en: 'contact' },
  plans: { de: 'preise', en: 'plans' },
  apis: { de: 'apis', en: 'apis' },
  analysis: { de: 'analyse', en: 'analysis' },
  rechnungen: { de: 'rechnungen', en: 'invoices' },
  'privacy-policy': { de: 'datenschutz', en: 'privacy-policy' },
  agb: { de: 'agb', en: 'terms-and-conditions' },
  products: { de: 'produkte', en: 'products' },
  risks: { de: 'risiken', en: 'risks' },
  opportunities: { de: 'chancen', en: 'opportunities' },
  login: { de: 'anmelden', en: 'login' },
  listen: { de: 'listen', en: 'lists' },
  // User-curated lists (Pro). Distinct from `listen`, the public SEO lists —
  // that segment owns /listen (de) and /lists (en), so this one cannot.
  'my-lists': { de: 'meine-listen', en: 'my-lists' },
  blog: { de: 'blog', en: 'blog' },
};

/**
 * Build reverse lookup: for each locale, map localized segment -> internal segment.
 */
function buildReverseMaps(): Record<Locale, Record<string, string>> {
  const result = {} as Record<Locale, Record<string, string>>;
  for (const locale of SUPPORTED_LOCALES) {
    result[locale] = {};
    for (const [internal, translations] of Object.entries(SEGMENT_MAP)) {
      result[locale][translations[locale]] = internal;
    }
  }
  return result;
}

const REVERSE_MAPS = buildReverseMaps();

/** Public first segments that were never ours, mapped to the internal route. */
const LEGACY_SEGMENTS: Readonly<Record<string, string>> = {
  pricing: 'plans',
  prices: 'plans',
  preis: 'plans',
};

/**
 * Check if a string is a supported locale.
 */
export function isLocale(value: string): value is Locale {
  return (SUPPORTED_LOCALES as readonly string[]).includes(value);
}

/**
 * Extract locale from the first path segment.
 * Returns DEFAULT_LOCALE if no valid locale prefix found.
 */
export function getLocaleFromPath(path: string): Locale {
  const segments = path.split('/').filter(Boolean);
  const first = segments[0];
  if (first && isLocale(first)) return first;
  return DEFAULT_LOCALE;
}

/**
 * Remove locale prefix from a path.
 * "/de/suche" -> "/suche", "/en/search" -> "/search"
 * If no locale prefix, returns path as-is.
 */
export function stripLocalePrefix(path: string): string {
  const segments = path.split('/').filter(Boolean);
  if (segments.length > 0 && isLocale(segments[0])) {
    const rest = segments.slice(1).join('/');
    return rest ? `/${rest}` : '/';
  }
  return path;
}

/**
 * Translate a single segment from localized form to internal form.
 */
function delocalizeSegment(segment: string, locale: Locale): string {
  const own = REVERSE_MAPS[locale][segment];
  if (own) return own;
  // The other locale's word ("/en/preise"), then words we never used but
  // people type ("/pricing" 308'd to /de/pricing, a 404, until 2026-11):
  // the canonical check then 308s to the localized URL.
  for (const other of SUPPORTED_LOCALES) {
    const internal = REVERSE_MAPS[other][segment];
    if (internal) return internal;
  }
  return LEGACY_SEGMENTS[segment] ?? segment;
}


/**
 * Translate a single segment from internal form to localized form.
 */
function localizeSegment(segment: string, locale: Locale): string {
  const mapping = SEGMENT_MAP[segment];
  return mapping ? mapping[locale] : segment;
}

/**
 * Convert a localized pathname to an internal pathname.
 * "/de/suche" -> { locale: 'de', pathname: '/search' }
 * "/en/industrie/62/slug" -> { locale: 'en', pathname: '/industry/62/slug' }
 */
export function delocalizePath(localizedPath: string): {
  locale: Locale;
  pathname: string;
} {
  const segments = localizedPath.split('/').filter(Boolean);

  if (segments.length === 0) {
    return { locale: DEFAULT_LOCALE, pathname: '/' };
  }

  const locale = isLocale(segments[0]) ? segments[0] : DEFAULT_LOCALE;
  const pathSegments = isLocale(segments[0]) ? segments.slice(1) : segments;

  if (pathSegments.length === 0) {
    return { locale, pathname: '/' };
  }

  const publicPath = `/${pathSegments.join('/')}`;
  const aliasLocales = [
    locale,
    ...SUPPORTED_LOCALES.filter((otherLocale) => otherLocale !== locale),
  ];
  for (const aliasLocale of aliasLocales) {
    const alias = PUBLIC_PREFIX_ALIASES[aliasLocale].find((entry) =>
      matchesPrefix(publicPath, entry.public[aliasLocale]),
    );
    if (alias) {
      // Category slugs stay as supplied, just as in legacy delocalization;
      // localizePathname re-slugs them for the canonical request locale.
      return {
        locale,
        pathname: `${alias.internal}${publicPath.slice(alias.public[aliasLocale].length)}`,
      };
    }
  }

  // Translate the first segment (route name)
  const firstDelocalized = delocalizeSegment(pathSegments[0], locale);
  const rest = pathSegments.slice(1);

  const parts = [firstDelocalized, ...rest].filter(Boolean);
  return { locale, pathname: `/${parts.join('/')}` };
}

/**
 * Convert an internal pathname to a localized pathname.
 * ("/search", "de") -> "/de/suche"
 * ("/industry/62/slug", "en") -> "/en/explore/industry/62/slug"
 */
export function localizePathname(internalPath: string, locale: Locale): string {
  if (internalPath === '/') {
    return `/${locale}`;
  }

  const segments = internalPath.split('/').filter(Boolean);

  // Strip locale prefix if accidentally included
  const startIdx = isLocale(segments[0]) ? 1 : 0;
  const pathSegments = segments.slice(startIdx);

  if (pathSegments.length === 0) {
    return `/${locale}`;
  }

  const path = `/${pathSegments.join('/')}`;
  const alias = INTERNAL_PREFIX_ALIASES.find((entry) =>
    matchesPrefix(path, entry.internal),
  );
  if (alias) {
    const rest = path.slice(alias.internal.length);
    const localizedRest = alias.internal === '/industry'
      ? rest.split('/').map((segment) => relocalizeIndustrySlug(segment, locale)).join('/')
      : rest;
    return `/${locale}${alias.public[locale]}${localizedRest}`;
  }

  // Translate the first segment
  const firstLocalized = localizeSegment(pathSegments[0], locale);
  const rest = pathSegments.slice(1);

  const parts = [firstLocalized, ...rest].filter(Boolean);
  return `/${locale}/${parts.join('/')}`;
}

/**
 * Get all internal segment names (for matching in middleware).
 */
export function getLocalizedSegments(locale: Locale): Record<string, string> {
  const result: Record<string, string> = {};
  for (const [internal, translations] of Object.entries(SEGMENT_MAP)) {
    result[translations[locale]] = internal;
  }
  return result;
}
